import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { 
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import ImageGalleryModal from './ImageGalleryModal';

const QuickChatSection = ({ projectId, onNavigateToChat }) => {
  const { user } = useAuthStore();
  const [allMessages, setAllMessages] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // 스크롤 관련 코드 제거 (미리보기 전용이므로 불필요)
  
  // 최근 메시지 10개 조회 (읽기 전용)
  const { isLoading } = useQuery(
    ['quickChatMessages', projectId], // 메인 채팅과 다른 키 사용
    () => api.get(`/messages/project/${projectId}?limit=500`).then(res => res.data),
    {
      enabled: !!projectId,
      refetchInterval: 3000, // 3초마다 새로고침 (실시간성 향상)
      refetchOnWindowFocus: true, // 창 포커스시 즉시 새로고침
      select: (data) => {
        // 모든 메시지를 가져와서 최신 10개만 추출
        const messages = data.messages || [];
        
        if (messages.length === 0) return [];
        
        // 메시지가 100개 이상이면 마지막 100개만 대상으로
        const targetMessages = messages.length > 100 ? messages.slice(-100) : messages;
        
        // 마지막 10개 추출 (최신 메시지들)
        const latest10 = targetMessages.slice(-10);
        
        return latest10;
      },
      onSuccess: (messages) => {
        setAllMessages(messages);
      },
      staleTime: 1000, // 1초 동안만 fresh 상태 유지
      cacheTime: 30 * 1000, // 30초간만 캐시 유지 (실시간성 향상)
    }
  );


  const formatMessageTime = (date) => {
    const messageDate = new Date(date);
    const now = new Date();
    const diffInMinutes = Math.floor((now - messageDate) / 1000 / 60);
    
    if (diffInMinutes < 1) return '방금 전';
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}시간 전`;
    return format(messageDate, 'MM/dd HH:mm');
  };

  // 사용자 정보가 없으면 로딩 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            프로젝트 실시간 소통
          </h3>
          {allMessages.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              최근 메시지 {allMessages.length}개
            </p>
          )}
        </div>
        <span className="text-xs text-gray-400">
          미리보기
        </span>
      </div>
      
      {/* 최근 메시지 미리보기 */}
      <div className="space-y-2 mb-4">
        {isLoading ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            메시지를 불러오는 중...
          </div>
        ) : allMessages && allMessages.length > 0 ? (
          allMessages.filter(msg => msg && msg.id).map((msg) => {
            const isMine = msg && user && msg.user_id === user.id;
            
            return (
              <div
                key={msg.id}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${isMine ? 'order-2' : 'order-1'}`}>
                  {!isMine && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                        {msg.User?.name?.[0] || '?'}
                      </div>
                      <span className="text-xs text-gray-600 font-medium">
                        {msg.User?.name || '알 수 없음'}
                      </span>
                    </div>
                  )}
                  <div
                    className={`rounded-lg px-3 py-2 ${
                      isMine
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {msg.content && (
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>
                    )}
                    
                    {/* 첨부파일 표시 - 이미지 그룹 레이아웃 개선 */}
                    {msg.MessageFiles && Array.isArray(msg.MessageFiles) && msg.MessageFiles.length > 0 && (() => {
                      const getFilePath = (path) => {
                        if (!path) return '';
                        if (path.startsWith('http')) return path;
                        
                        // 개발 환경에서는 백엔드 서버 주소를 붙여줌
                        const baseUrl = process.env.NODE_ENV === 'development' 
                          ? 'http://localhost:5000' 
                          : '';
                        
                        // 경로가 /로 시작하면 그대로 사용
                        if (path.startsWith('/')) return baseUrl + path;
                        // uploads/로 시작하면 앞에 / 추가
                        return baseUrl + '/' + path;
                      };
                      
                      const imageFiles = msg.MessageFiles.filter(f => f.file_type === 'image');
                      const docFiles = msg.MessageFiles.filter(f => f.file_type !== 'image');
                      
                      return (
                        <div className="mt-2">
                          {/* 이미지 파일 그리드 */}
                          {imageFiles.length > 0 && (
                            <div className={`${imageFiles.length === 1 ? 'block' : 'grid grid-cols-2 gap-1'} max-w-xs`}>
                              {imageFiles.slice(0, 4).map((file, idx) => (
                                <div key={file.id || idx} className="relative">
                                  <picture
                                    className="block cursor-pointer"
                                    onClick={() => {
                                      // 이미지 경로 포맷팅
                                      const formattedImages = imageFiles.map(img => {
                                        const path = img.file_path;
                                        if (!path) return '';
                                        if (path.startsWith('http')) return path;
                                        if (path.startsWith('/')) return path;
                                        if (path.startsWith('uploads/')) return '/' + path;
                                        return '/' + path;
                                      });
                                      setGalleryImages(formattedImages);
                                      setCurrentImageIndex(idx);
                                      setShowImageModal(true);
                                    }}
                                  >
                                    {file.thumbnail_medium_webp && (
                                      <source 
                                        srcSet={getFilePath(file.thumbnail_medium_webp)}
                                        type="image/webp" 
                                      />
                                    )}
                                    <img 
                                      src={getFilePath(file.thumbnail_medium || file.file_path)}
                                      alt={file.file_name}
                                      className={`rounded ${
                                        imageFiles.length === 1 
                                          ? 'w-full h-auto max-h-32' 
                                          : 'w-full h-20 object-cover'
                                      }`}
                                      loading="lazy"
                                    />
                                  </picture>
                                  {/* 4번째 이미지에 +N 오버레이 */}
                                  {idx === 3 && imageFiles.length > 4 && (
                                    <div 
                                      className="absolute inset-0 bg-black/60 rounded flex items-center justify-center cursor-pointer"
                                      onClick={() => {
                                        // 이미지 경로 포맷팅
                                        const formattedImages = imageFiles.map(img => {
                                          const path = img.file_path;
                                          if (!path) return '';
                                          if (path.startsWith('http')) return path;
                                          if (path.startsWith('/')) return path;
                                          if (path.startsWith('uploads/')) return '/' + path;
                                          return '/' + path;
                                        });
                                        setGalleryImages(formattedImages);
                                        setCurrentImageIndex(3);
                                        setShowImageModal(true);
                                      }}
                                    >
                                      <span className="text-white font-bold text-lg">
                                        +{imageFiles.length - 4}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* 문서 파일 목록 */}
                          {docFiles.length > 0 && (
                            <div className={`${imageFiles.length > 0 ? 'mt-1' : ''} space-y-1`}>
                              {docFiles.map((file, index) => (
                                <div key={file.id || index} className={`flex items-center gap-2 p-2 rounded text-xs ${
                                  isMine ? 'bg-blue-600 bg-opacity-30' : 'bg-gray-200'
                                }`}>
                                  <PaperClipIcon className="h-3 w-3" />
                                  <span className="truncate">{file.file_name || '파일'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    <p className={`text-xs mt-1 ${
                      isMine ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {msg.created_at ? formatMessageTime(msg.created_at) : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-500">
            <ChatBubbleLeftRightIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">아직 대화가 없습니다</p>
            <p className="text-xs mt-1">첫 메시지를 보내보세요!</p>
          </div>
        )}
      </div>
      
      {/* 전체 대화 보기 버튼 */}
      <button
        onClick={onNavigateToChat}
        className="w-full mt-4 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <ChatBubbleLeftRightIcon className="h-5 w-5" />
        전체 대화 보기
        <ArrowRightIcon className="h-4 w-4" />
      </button>
      
      <p className="text-xs text-gray-500 mt-2 text-center">
        메시지를 작성하려면 전체 대화 보기를 클릭하세요
      </p>
      
      {/* 이미지 갤러리 모달 - ImageGalleryModal 컴포넌트 사용 */}
      <ImageGalleryModal 
        isOpen={showImageModal}
        images={galleryImages}
        currentIndex={currentImageIndex}
        projectId={projectId}
        onClose={() => {
          setShowImageModal(false);
          setGalleryImages([]);
          setCurrentImageIndex(0);
        }}
        onIndexChange={(index) => {
          setCurrentImageIndex(index);
        }}
      />
    </div>
  );
};

export default QuickChatSection;